import { DestinationAdapter } from './base-adapter.js';

const GA4_SCRIPT_URL = 'https://www.googletagmanager.com/gtag/js';
const SCRIPT_LOAD_TIMEOUT_MS = 10000;

class GA4Adapter extends DestinationAdapter {
  constructor() {
    super();
    this._vendorLoaded = false;
    this._loadPromise = null;
  }

  get name() {
    return 'GA4';
  }

  isInitialized() {
    return this._vendorLoaded && typeof window.gtag === 'function';
  }

  _hasExistingUsableGtag() {
    return typeof window.gtag === 'function' && !window.gtag.__eventfoundryStub;
  }

  async _loadVendorScript(measurementId) {
    if (this._loadPromise) {
      return this._loadPromise;
    }

    this._loadPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      let settled = false;

      script.src = `${GA4_SCRIPT_URL}?id=${measurementId}`;
      script.async = true;

      script.onload = () => {
        if (settled) return;
        settled = true;
        this._vendorLoaded = true;
        resolve(true);
      };

      script.onerror = () => {
        if (settled) return;
        settled = true;
        console.error('EventFoundry: Failed to load gtag.js');
        resolve(false);
      };

      setTimeout(() => {
        if (settled) return;
        settled = true;
        console.error('EventFoundry: gtag.js load timeout');
        resolve(false);
      }, SCRIPT_LOAD_TIMEOUT_MS);

      document.head.appendChild(script);
    });

    const loaded = await this._loadPromise;
    this._loadPromise = null;
    return loaded;
  }

  async initialize(credentials) {
    const { measurementId } = credentials;

    if (!measurementId) {
      console.error('EventFoundry: GA4 adapter missing measurementId');
      return false;
    }

    // Validate measurement ID format
    if (!measurementId.match(/^G-[A-Z0-9]+$/)) {
      console.error(`EventFoundry: Invalid GA4 measurement ID format: ${measurementId}`);
      return false;
    }

    if (this._hasExistingUsableGtag()) {
      this._vendorLoaded = true;
      console.log('EventFoundry: Using existing GA4 installation');
      return true;
    }

    // Install queueing stub before script load.
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag.__eventfoundryStub = true;
    }

    const vendorLoaded = await this._loadVendorScript(measurementId);
    if (!vendorLoaded) {
      this._vendorLoaded = false;
      console.error('EventFoundry: GA4 vendor script not available');
      return false;
    }

    // Configure GA4
    window.gtag('js', new Date());
    window.gtag('config', measurementId);

    console.log('EventFoundry: GA4 initialized successfully');
    return true;
  }

  fireEvent(eventKey, eventDef, params) {
    if (!this.isInitialized()) {
      console.error('EventFoundry: GA4 not initialized - cannot fire event');
      return;
    }

    // Fire event with beacon transport for reliable delivery.
    // Adapters own destination-specific enrichment — event_label and event_category added here.
    window.gtag('event', eventKey, {
      ...params,
      event_label: eventDef.eventLabel,
      event_category: 'EventFoundry',
      transport_type: 'beacon'
    });
  }
}

export const ga4Adapter = new GA4Adapter();
